"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

type LandingPageRendererProps = {
  title: string;
  html: string;
  css: string;
};

function isDomElement(value: unknown): value is Element {
  return (
    typeof value === "object" &&
    value !== null &&
    "nodeType" in value &&
    (value as { nodeType: number }).nodeType === 1
  );
}

export function LandingPageRenderer({
  title,
  html,
  css,
}: LandingPageRendererProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const source = useMemo(
    () => `<!doctype html>
<html lang="hu">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title.replaceAll("<", "&lt;").replaceAll(">", "&gt;")}</title>
    <style>${css}</style>
  </head>
  <body>${html}</body>
</html>`,
    [css, html, title],
  );

  const syncHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const frameWindow = iframe?.contentWindow;
    const frameDocument = iframe?.contentDocument;
    if (!iframe || !frameWindow || !frameDocument) {
      return;
    }

    const root = frameDocument.documentElement;
    const body = frameDocument.body;
    if (!isDomElement(root)) {
      return;
    }

    const updateHeight = () => {
      const height = Math.max(
        root.scrollHeight,
        body?.scrollHeight ?? 0,
        frameWindow.innerHeight,
        window.innerHeight,
      );
      iframe.style.height = `${height}px`;
    };

    try {
      observerRef.current?.disconnect();
    } catch {
      observerRef.current = null;
    }

    const FrameResizeObserver = (
      frameWindow as Window & { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;
    if (typeof FrameResizeObserver !== "function") {
      updateHeight();
      return;
    }

    observerRef.current = new FrameResizeObserver(updateHeight);
    observerRef.current.observe(root);
    if (isDomElement(body)) {
      observerRef.current.observe(body);
    }
    frameDocument.querySelectorAll("img").forEach((image) => {
      image.addEventListener("load", updateHeight, { once: true });
    });
    updateHeight();
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(syncHeight);
    const timeout = window.setTimeout(syncHeight, 250);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      try {
        observerRef.current?.disconnect();
      } catch {
        observerRef.current = null;
      }
    };
  }, [source, syncHeight]);

  return (
    <iframe
      ref={iframeRef}
      title={title}
      srcDoc={source}
      sandbox="allow-same-origin allow-top-navigation-by-user-activation"
      className="block min-h-screen w-full border-0 bg-white"
      onLoad={syncHeight}
    />
  );
}
